import React, { useState, useEffect } from "react";
import { C, ROLE_PERMISSIONS } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import {
  Heart, Users, UserPlus, BookOpen, GraduationCap, Calendar,
  CheckCircle, Clock, Plus, Search, Star, Target,
  Phone, Mail, FileText, TrendingUp, Award, MapPin,
  Layers, Mic, Baby, Flower2, Sparkles
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Women Page — القسم النسائي
   ═════════════════════════════════════════════ */
export default function WomenPage({ user }) {
  const [tab, setTab] = useState("overview");
  const [triggers, setTriggers] = useState({ program: 0, circle: 0, teacher: 0, beneficiary: 0 });
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "overview", label: "نظرة عامة", icon: Heart },
    { key: "programs", label: "البرامج", icon: BookOpen },
    { key: "beneficiaries", label: "المستفيدات", icon: Users },
    { key: "teachers", label: "المعلمات", icon: GraduationCap },
    { key: "circles", label: "الحلقات", icon: Layers },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  return (
    <div>
      <ModuleHeader icon={Heart} title="القسم النسائي" subtitle="إدارة البرامج والحلقات النسائية والمعلمات والمستفيدات" />

      {perms.canEdit && (
        <div style={wq.row}>
          <button style={wq.btn} onClick={() => fireQuick("programs", "program")}>
            <BookOpen size={16} color="#db2777" />
            <span>تأسيس برنامج جديد</span>
          </button>
          <button style={wq.btn} onClick={() => fireQuick("circles", "circle")}>
            <Layers size={16} color="#7c3aed" />
            <span>إعداد حلقة جديدة</span>
          </button>
          <button style={wq.btn} onClick={() => fireQuick("teachers", "teacher")}>
            <GraduationCap size={16} color="#0891b2" />
            <span>إضافة معلمة جديدة</span>
          </button>
          <button style={wq.btn} onClick={() => fireQuick("beneficiaries", "beneficiary")}>
            <UserPlus size={16} color={C.teal} />
            <span>إضافة مستفيدة جديدة</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview" && <OverviewTab />}
      {tab === "programs" && <ProgramsTab canEdit={perms.canEdit} triggerOpen={triggers.program} />}
      {tab === "beneficiaries" && <BeneficiariesTab canEdit={perms.canEdit} triggerOpen={triggers.beneficiary} />}
      {tab === "teachers" && <TeachersTab canEdit={perms.canEdit} triggerOpen={triggers.teacher} />}
      {tab === "circles" && <CirclesTab canEdit={perms.canEdit} triggerOpen={triggers.circle} />}
      {tab === "meetings" && <MeetingsTab departmentName="القسم النسائي" />}
      {tab === "transactions" && <TransactionsTab budget={380000} />}
      {tab === "plan" && <OperationalPlanTab departmentKey="women" />}
      {tab === "reports" && <ReportsTab departmentName="القسم النسائي" />}
    </div>
  );
}

const wq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: C.white, color: C.primary, border: `1px solid ${C.border}`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

/* ─────────────────────────────────────────────
   ✦ Overview Tab
   ───────────────────────────────────────────── */
function OverviewTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="المستفيدات" value="1,800" icon={Users} color="#db2777" trend="+15%" />
        <KPICard title="البرامج النشطة" value="8" icon={BookOpen} color="#7c3aed" />
        <KPICard title="المعلمات" value="14" icon={GraduationCap} color={C.teal} />
        <KPICard title="الحلقات" value="16" icon={Layers} color="#2563eb" />
        <KPICard title="الميزانية (ريال)" value="380,000" icon={TrendingUp} color={C.accent} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Programs Summary */}
        <Card style={{ padding: 20 }}>
          <h4 style={styles.sectionTitle}><Sparkles size={18} color="#db2777" /> أبرز البرامج</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {PROGRAMS_HIGHLIGHT.map((p, i) => (
              <div key={i} style={styles.highlightCard}>
                <div style={{ ...styles.highlightIcon, background: `${p.color}15` }}>
                  <p.icon size={18} color={p.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary }}>{p.name}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{p.stat}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming Activities */}
        <Card style={{ padding: 20 }}>
          <h4 style={styles.sectionTitle}><Calendar size={18} color="#ea580c" /> الأنشطة القادمة</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {UPCOMING_ACTIVITIES.map((a, i) => (
              <div key={i} style={styles.activityCard}>
                <div style={{ ...styles.activityDate, borderColor: a.color }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: C.primary, lineHeight: 1 }}>{a.day}</span>
                  <span style={{ fontSize: 10, color: C.textLight }}>{a.month}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary }}>{a.title}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{a.time} — {a.location}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Programs Tab
   ───────────────────────────────────────────── */
const INITIAL_WPROG = { name: "", type: "", teacher: "", schedule: "", beneficiaries: "", description: "", statusLabel: "مخطط" };
const WPROG_STATUS_LABEL_TO_KEY = { "نشط": "active", "مخطط": "planned", "منتهي": "completed" };

function ProgramsTab({ canEdit, triggerOpen = 0 }) {
  const [programs, setPrograms] = useState(DEMO_PROGRAMS);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_WPROG);

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);
  const [showToast, ToastView] = useToast();

  const filtered = programs.filter(p => p.name.includes(search) || p.type.includes(search));

  function handleSave() {
    if (!form.name || !form.type) {
      showToast("الاسم والنوع مطلوبان", "error");
      return;
    }
    const newP = {
      id: `wp${Date.now()}`,
      name: form.name,
      type: form.type,
      beneficiaries: Number(form.beneficiaries) || 0,
      teacher: form.teacher || "—",
      schedule: form.schedule || "",
      status: WPROG_STATUS_LABEL_TO_KEY[form.statusLabel] || "planned",
      description: form.description,
    };
    setPrograms(prev => [...prev, newP]);
    setForm(INITIAL_WPROG);
    setShowAdd(false);
    showToast("تمت إضافة البرنامج");
  }

  const PROG_STATUSES = [
    { key: "active", label: "نشط", color: C.success },
    { key: "planned", label: "مخطط", color: C.warning },
    { key: "completed", label: "منتهي", color: C.textLight },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي البرامج" value={programs.length} icon={BookOpen} color="#7c3aed" />
        <KPICard title="نشطة" value={programs.filter(p => p.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="المستفيدات" value="1,800" icon={Users} color="#db2777" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">برنامج جديد</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "اسم البرنامج", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "type", label: "النوع", width: "120px" },
            { key: "beneficiaries", label: "المستفيدات", width: "100px", render: v => v.toLocaleString() },
            { key: "teacher", label: "المعلمة", width: "140px" },
            { key: "schedule", label: "الموعد", width: "130px" },
            { key: "status", label: "الحالة", width: "100px", render: v => <StatusBadge status={v} statuses={PROG_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>المعلمة:</strong> {selected.teacher}</div>
            <div style={styles.detailRow}><strong>الموعد:</strong> {selected.schedule}</div>
            <div style={styles.detailRow}><strong>المستفيدات:</strong> {selected.beneficiaries.toLocaleString()}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة برنامج نسائي" onClose={() => { setShowAdd(false); setForm(INITIAL_WPROG); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم البرنامج" required placeholder="مثال: حلقة تحفيظ القرآن" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["حلقات قرآن","دروس شرعية","دورات تطويرية","محاضرات","برامج أطفال","أنشطة اجتماعية"]} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="المعلمة" placeholder="اسم المعلمة" value={form.teacher} onChange={v => setForm({ ...form, teacher: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الموعد" placeholder="مثال: الأحد والثلاثاء 4-6م" value={form.schedule} onChange={v => setForm({ ...form, schedule: v })} />
              <Field label="عدد المستفيدات المتوقع" type="number" placeholder="0" value={form.beneficiaries} onChange={v => setForm({ ...form, beneficiaries: v })} />
              <Field label="الحالة" type="select" options={["نشط","مخطط","منتهي"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <Field label="الوصف" type="textarea" placeholder="وصف تفصيلي للبرنامج..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={BookOpen} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_WPROG); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Beneficiaries Tab
   ───────────────────────────────────────────── */
const INITIAL_WBEN = { name: "", nationality: "", age: "", phone: "", program: "", circle: "", registrationDate: "" };

function BeneficiariesTab({ canEdit, triggerOpen = 0 }) {
  const [data, setData] = useState(DEMO_BENEFICIARIES);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_WBEN);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = data.filter(b => b.name.includes(search) || b.program.includes(search));

  function handleSave() {
    if (!form.name) {
      showToast("الاسم مطلوب", "error");
      return;
    }
    const newB = {
      id: `wb${Date.now()}`,
      name: form.name,
      nationality: form.nationality || "—",
      age: Number(form.age) || 0,
      program: form.program || "—",
      circle: form.circle || "—",
      joinDate: form.registrationDate || new Date().toISOString().slice(0, 10),
      attendance: 100,
    };
    setData(prev => [...prev, newB]);
    setForm(INITIAL_WBEN);
    setShowAdd(false);
    showToast("تمت إضافة المستفيدة");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المستفيدات" value={data.length} icon={Users} color="#db2777" />
        <KPICard title="سعوديات" value={data.filter(b => b.nationality === "سعودية").length} icon={Star} color={C.accent} />
        <KPICard title="غير سعوديات" value={data.filter(b => b.nationality !== "سعودية").length} icon={MapPin} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مستفيدة</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "الاسم", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "nationality", label: "الجنسية", width: "100px" },
            { key: "age", label: "العمر", width: "70px" },
            { key: "program", label: "البرنامج", width: "150px" },
            { key: "circle", label: "الحلقة", width: "130px" },
            { key: "joinDate", label: "التسجيل", width: "100px" },
            { key: "attendance", label: "الحضور", width: "90px", render: v => (
              <span style={{ color: v >= 80 ? C.success : v >= 60 ? C.warning : C.danger, fontWeight: 600 }}>{v}%</span>
            )},
          ]}
          data={filtered}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة مستفيدة" onClose={() => { setShowAdd(false); setForm(INITIAL_WBEN); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم الكامل" required placeholder="اسم المستفيدة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الجنسية" type="select" options={["سعودية","مصرية","سورية","يمنية","أردنية","فلبينية","إندونيسية","أخرى"]} value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="العمر" type="number" placeholder="0" value={form.age} onChange={v => setForm({ ...form, age: v })} />
              <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="البرنامج" type="select" options={DEMO_PROGRAMS.map(p => p.name)} value={form.program} onChange={v => setForm({ ...form, program: v })} />
              <Field label="الحلقة" type="select" options={DEMO_CIRCLES.map(c => c.name)} value={form.circle} onChange={v => setForm({ ...form, circle: v })} />
              <Field label="تاريخ التسجيل" type="date" value={form.registrationDate} onChange={v => setForm({ ...form, registrationDate: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_WBEN); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Teachers Tab
   ───────────────────────────────────────────── */
const TEACHER_SPECIALTIES = ["تحفيظ قرآن", "تجويد", "فقه", "عقيدة", "تفسير", "لغة عربية", "تربية أطفال"];
const TEACHER_PROGRAMS_LIST = ["تحفيظ القرآن", "التجويد", "الفقه الميسر", "العقيدة", "تربية الأبناء", "حفظ الأطفال", "فن الخياطة"];
const INITIAL_TEACHER = {
  name: "", specialty: "", qualification: "", typeLabel: "متفرغة",
  phone: "", email: "",
  circles: "", students: "",
  hireDate: "", programs: [],
  priorExperience: "", notes: "",
};

function toggleTeacherProgram(setForm, p) {
  setForm(prev => ({
    ...prev,
    programs: prev.programs.includes(p) ? prev.programs.filter(x => x !== p) : [...prev.programs, p],
  }));
}

function TeachersTab({ canEdit, triggerOpen = 0 }) {
  const [teachers, setTeachers] = useState(DEMO_TEACHERS);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_TEACHER);

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);
  const [showToast, ToastView] = useToast();

  const filtered = teachers.filter(t => t.name.includes(search) || t.specialty.includes(search));

  function handleSave() {
    if (!form.name || !form.specialty) {
      showToast("الاسم والتخصص مطلوبان", "error");
      return;
    }
    const newT = {
      id: `t${Date.now()}`,
      name: form.name,
      specialty: form.specialty,
      qualification: form.qualification || "—",
      type: form.typeLabel === "متفرغة" ? "fulltime" : "parttime",
      phone: form.phone,
      email: form.email,
      circles: Number(form.circles) || 0,
      students: Number(form.students) || 0,
      hireDate: form.hireDate,
      programs: [...form.programs],
      priorExperience: form.priorExperience,
      notes: form.notes,
    };
    setTeachers(prev => [...prev, newT]);
    setForm(INITIAL_TEACHER);
    setShowAdd(false);
    showToast("تمت إضافة المعلمة");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المعلمات" value={teachers.length} icon={GraduationCap} color={C.teal} />
        <KPICard title="متفرغات" value={teachers.filter(t => t.type === "fulltime").length} icon={CheckCircle} color={C.success} />
        <KPICard title="متعاونات" value={teachers.filter(t => t.type === "parttime").length} icon={Clock} color={C.warning} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة معلمة</Btn>}
        </div>
      </Card>

      <div style={styles.teachersGrid}>
        {filtered.map(t => (
          <Card key={t.id} style={styles.teacherCard} onClick={() => setSelected(t)}>
            <div style={styles.teacherTop}>
              <div style={styles.teacherAvatar}>{t.name.charAt(0)}</div>
              <span style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: t.type === "fulltime" ? `${C.success}15` : `${C.warning}15`,
                color: t.type === "fulltime" ? C.success : C.warning,
              }}>
                {t.type === "fulltime" ? "متفرغة" : "متعاونة"}
              </span>
            </div>
            <h4 style={{ margin: "10px 0 2px", fontSize: 15, fontWeight: 700, color: C.primary }}>{t.name}</h4>
            <p style={{ margin: 0, fontSize: 13, color: "#db2777", fontWeight: 500 }}>{t.specialty}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
              {t.programs.map((p, i) => <span key={i} style={styles.progTag}>{p}</span>)}
            </div>
            <div style={styles.teacherStats}>
              <span><Users size={12} /> {t.students} طالبة</span>
              <span><Layers size={12} /> {t.circles} حلقة</span>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={`المعلمة — ${selected.name}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>التخصص:</strong> {selected.specialty}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type === "fulltime" ? "متفرغة" : "متعاونة"}</div>
            <div style={styles.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>
            <div style={styles.detailRow}><strong>عدد الطالبات:</strong> {selected.students}</div>
            <div style={styles.detailRow}><strong>عدد الحلقات:</strong> {selected.circles}</div>
            <div style={styles.detailRow}><strong>المؤهل:</strong> {selected.qualification}</div>
            <div style={styles.detailRow}>
              <strong>البرامج:</strong>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                {selected.programs.map((p, i) => <span key={i} style={styles.progTag}>{p}</span>)}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة معلمة" onClose={() => { setShowAdd(false); setForm(INITIAL_TEACHER); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="الاسم الكامل" required placeholder="اسم المعلمة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="التخصص" type="select" options={TEACHER_SPECIALTIES} required value={form.specialty} onChange={v => setForm({ ...form, specialty: v })} />
              <Field label="المؤهل" placeholder="بكالوريوس / ماجستير..." value={form.qualification} onChange={v => setForm({ ...form, qualification: v })} />
              <Field label="نوع التعاقد" type="select" options={["متفرغة","متعاونة"]} required value={form.typeLabel} onChange={v => setForm({ ...form, typeLabel: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="رقم الجوال" placeholder="05xxxxxxxx" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="البريد الإلكتروني" placeholder="email@example.com" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="عدد الحلقات" type="number" placeholder="0" value={form.circles} onChange={v => setForm({ ...form, circles: v })} />
              <Field label="عدد الطالبات" type="number" placeholder="0" value={form.students} onChange={v => setForm({ ...form, students: v })} />
              <Field label="تاريخ التعيين" type="date" value={form.hireDate} onChange={v => setForm({ ...form, hireDate: v })} />
            </div>

            {/* Programs multi-select */}
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                البرامج المسؤولة عنها ({form.programs.length} مختار)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                {TEACHER_PROGRAMS_LIST.map(p => (
                  <label key={p} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.programs.includes(p)} onChange={() => toggleTeacherProgram(setForm, p)} />
                    <span>{p}</span>
                  </label>
                ))}
              </div>
            </div>

            <Field label="الخبرات السابقة" type="textarea" rows={2} placeholder="اذكري خبراتك السابقة..." value={form.priorExperience} onChange={v => setForm({ ...form, priorExperience: v })} />
            <Field label="ملاحظات" type="textarea" rows={2} placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={GraduationCap} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_TEACHER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Circles Tab — الحلقات
   ───────────────────────────────────────────── */
const INITIAL_CIRCLE = { name: "", type: "تحفيظ", teacher: "", schedule: "", location: "", curriculum: "", statusLabel: "نشطة" };
const CIRCLE_STATUS_LABEL_TO_KEY = { "نشطة": "active", "معلّقة": "paused", "منتهية": "completed" };

function CirclesTab({ canEdit, triggerOpen = 0 }) {
  const [circles, setCircles] = useState(DEMO_CIRCLES);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_CIRCLE);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  function handleSave() {
    if (!form.name || !form.teacher) {
      showToast("الاسم والمعلمة مطلوبان", "error");
      return;
    }
    const newC = {
      id: `cr${Date.now()}`,
      name: form.name,
      type: form.type,
      teacher: form.teacher,
      students: 0,
      schedule: form.schedule || "",
      location: form.location || "",
      progress: 0,
      curriculum: form.curriculum || "",
      status: CIRCLE_STATUS_LABEL_TO_KEY[form.statusLabel] || "active",
      color: "#db2777",
    };
    setCircles(prev => [...prev, newC]);
    setForm(INITIAL_CIRCLE);
    setShowAdd(false);
    showToast("تم إنشاء الحلقة");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="عدد الحلقات" value={circles.length} icon={Layers} color="#2563eb" />
        <KPICard title="إجمالي الطالبات" value={circles.reduce((s, c) => s + c.students, 0)} icon={Users} color="#db2777" />
        <KPICard title="حلقات القرآن" value={circles.filter(c => c.type === "تحفيظ").length} icon={BookOpen} color={C.teal} />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إنشاء حلقة</Btn>
        </div>
      )}

      <div style={styles.circlesGrid}>
        {circles.map(c => (
          <Card key={c.id} style={styles.circleCard} onClick={() => setSelected(c)}>
            <div style={styles.circleHeader}>
              <div style={{ ...styles.circleIcon, background: `${c.color}15` }}>
                <Layers size={20} color={c.color} />
              </div>
              <span style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: `${c.color}12`, color: c.color,
              }}>{c.type}</span>
            </div>
            <h4 style={{ margin: "12px 0 4px", fontSize: 15, fontWeight: 700, color: C.primary }}>{c.name}</h4>
            <p style={{ margin: 0, fontSize: 13, color: C.textLight }}>{c.teacher}</p>
            <div style={styles.circleStats}>
              <span><Users size={13} /> {c.students} طالبة</span>
              <span><Calendar size={13} /> {c.schedule}</span>
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={styles.progressBg}>
                <div style={{ ...styles.progressFill, width: `${c.progress}%`, background: c.color }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: 11, color: C.textLight }}>التقدم</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: c.color }}>{c.progress}%</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={`حلقة — ${selected.name}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>المعلمة:</strong> {selected.teacher}</div>
            <div style={styles.detailRow}><strong>عدد الطالبات:</strong> {selected.students}</div>
            <div style={styles.detailRow}><strong>الموعد:</strong> {selected.schedule}</div>
            <div style={styles.detailRow}><strong>المكان:</strong> {selected.location}</div>
            <div style={styles.detailRow}><strong>التقدم:</strong> {selected.progress}%</div>
            <div style={styles.detailRow}><strong>المنهج:</strong> {selected.curriculum}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إنشاء حلقة جديدة" onClose={() => { setShowAdd(false); setForm(INITIAL_CIRCLE); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم الحلقة" required placeholder="مثال: حلقة الفجر" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["تحفيظ","تجويد","تفسير","فقه","سيرة"]} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="المعلمة" type="select" options={DEMO_TEACHERS.map(t => t.name)} required value={form.teacher} onChange={v => setForm({ ...form, teacher: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الموعد" placeholder="الأحد والثلاثاء 4-6م" value={form.schedule} onChange={v => setForm({ ...form, schedule: v })} />
              <Field label="المكان" placeholder="مصلى النساء — مسجد التوحيد" value={form.location} onChange={v => setForm({ ...form, location: v })} />
            </div>
            <Field label="المنهج" type="textarea" placeholder="تفاصيل المنهج المقرر..." rows={3} value={form.curriculum} onChange={v => setForm({ ...form, curriculum: v })} />
            <Field label="الحالة" type="select" options={["نشطة","معلّقة","منتهية"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Layers} onClick={handleSave}>إنشاء</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_CIRCLE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const PROGRAMS_HIGHLIGHT = [
  { name: "حلقات تحفيظ القرآن", stat: "8 حلقات • 420 طالبة", icon: BookOpen, color: "#db2777" },
  { name: "دروس الفقه والعقيدة", stat: "4 دروس أسبوعية • 280 مستفيدة", icon: Star, color: "#7c3aed" },
  { name: "برنامج تربية الأبناء", stat: "دورة شهرية • 150 أم", icon: Baby, color: "#ea580c" },
  { name: "ملتقى المرأة المسلمة", stat: "ربع سنوي • 400 حاضرة", icon: Flower2, color: C.teal },
];

const UPCOMING_ACTIVITIES = [
  { title: "دورة أحكام الصيام للنساء", day: "18", month: "شعبان", time: "4:30م", location: "مصلى النساء", color: "#db2777" },
  { title: "ملتقى الأم المربية", day: "25", month: "شعبان", time: "5:00م", location: "قاعة الجمعية", color: "#7c3aed" },
  { title: "مسابقة حفظ جزء عم", day: "5", month: "رمضان", time: "10:00ص", location: "مسجد التوحيد", color: C.teal },
  { title: "إفطار جماعي نسائي", day: "15", month: "رمضان", time: "6:00م", location: "صالة المناسبات", color: "#ea580c" },
];

const DEMO_PROGRAMS = [
  { id: "wp1", name: "حلقات تحفيظ القرآن الكريم", type: "حلقات قرآن", beneficiaries: 420, teacher: "أ. نورة الدوسري", schedule: "يومياً 4-6م", status: "active", description: "برنامج تحفيظ القرآن الكريم للنساء بمختلف المستويات" },
  { id: "wp2", name: "دروس التجويد", type: "حلقات قرآن", beneficiaries: 180, teacher: "أ. هند المطيري", schedule: "الأحد والأربعاء 4-5م", status: "active", description: "دروس في أحكام التجويد النظرية والتطبيقية" },
  { id: "wp3", name: "دروس الفقه الميسر", type: "دروس شرعية", beneficiaries: 150, teacher: "أ. سارة العتيبي", schedule: "الاثنين 5-6:30م", status: "active", description: "شرح مبسط لفقه العبادات والمعاملات" },
  { id: "wp4", name: "برنامج تربية الأبناء", type: "دورات تطويرية", beneficiaries: 120, teacher: "أ. منال الحربي", schedule: "السبت 4-6م", status: "active", description: "دورات في التربية الإيجابية وبناء شخصية الطفل المسلم" },
  { id: "wp5", name: "دورة العقيدة الميسرة", type: "دروس شرعية", beneficiaries: 130, teacher: "أ. عبير الشمري", schedule: "الثلاثاء 5-6م", status: "active", description: "شرح أصول العقيدة الإسلامية بأسلوب مبسط" },
  { id: "wp6", name: "ملتقى المرأة المسلمة", type: "محاضرات", beneficiaries: 400, teacher: "متعددة", schedule: "ربع سنوي", status: "active", description: "ملتقى دوري يجمع المستفيدات بمحاضرات وورش عمل متنوعة" },
  { id: "wp7", name: "برنامج حفظ الأطفال", type: "برامج أطفال", beneficiaries: 200, teacher: "أ. فاطمة القحطاني", schedule: "السبت والاثنين 3-4:30م", status: "active", description: "تحفيظ القرآن لأبناء المستفيدات (4-10 سنوات)" },
  { id: "wp8", name: "دورة فن الخياطة", type: "دورات تطويرية", beneficiaries: 80, teacher: "أ. ريم السالم", schedule: "الأربعاء 3-5م", status: "planned", description: "دورة مهنية في الخياطة والتفصيل لتمكين المرأة اقتصادياً" },
];

const DEMO_TEACHERS = [
  { id: "t1", name: "أ. نورة الدوسري", specialty: "تحفيظ قرآن", type: "fulltime", phone: "0551112200", students: 120, circles: 3, qualification: "بكالوريوس دراسات قرآنية", programs: ["تحفيظ القرآن","التجويد"] },
  { id: "t2", name: "أ. هند المطيري", specialty: "تجويد", type: "fulltime", phone: "0552223300", students: 80, circles: 2, qualification: "إجازة في القراءات", programs: ["التجويد","حفظ الأطفال"] },
  { id: "t3", name: "أ. سارة العتيبي", specialty: "فقه", type: "parttime", phone: "0553334400", students: 75, circles: 2, qualification: "ماجستير شريعة", programs: ["الفقه الميسر","العقيدة"] },
  { id: "t4", name: "أ. منال الحربي", specialty: "تربية أطفال", type: "parttime", phone: "0554445500", students: 60, circles: 1, qualification: "بكالوريوس تربية", programs: ["تربية الأبناء"] },
  { id: "t5", name: "أ. عبير الشمري", specialty: "عقيدة", type: "fulltime", phone: "0555556600", students: 65, circles: 2, qualification: "ماجستير عقيدة", programs: ["العقيدة الميسرة","دروس شرعية"] },
  { id: "t6", name: "أ. فاطمة القحطاني", specialty: "تحفيظ أطفال", type: "fulltime", phone: "0556667700", students: 100, circles: 3, qualification: "دبلوم تحفيظ + تربية طفل", programs: ["حفظ الأطفال"] },
  { id: "t7", name: "أ. ريم السالم", specialty: "تدريب مهني", type: "parttime", phone: "0557778800", students: 40, circles: 1, qualification: "دبلوم تصميم أزياء", programs: ["فن الخياطة"] },
];

const DEMO_BENEFICIARIES = [
  { id: "wb1", name: "فاطمة الزهراني", nationality: "سعودية", age: 32, program: "تحفيظ القرآن", circle: "حلقة الفجر", joinDate: "1446/01/10", attendance: 92 },
  { id: "wb2", name: "نورة العسيري", nationality: "سعودية", age: 28, program: "التجويد", circle: "حلقة النور", joinDate: "1446/01/15", attendance: 88 },
  { id: "wb3", name: "منى الغامدي", nationality: "سعودية", age: 45, program: "الفقه الميسر", circle: "حلقة الهدى", joinDate: "1446/02/01", attendance: 75 },
  { id: "wb4", name: "سلمى المصري", nationality: "مصرية", age: 35, program: "تحفيظ القرآن", circle: "حلقة الفجر", joinDate: "1446/02/10", attendance: 95 },
  { id: "wb5", name: "هدى السورية", nationality: "سورية", age: 30, program: "تربية الأبناء", circle: "—", joinDate: "1446/03/01", attendance: 70 },
  { id: "wb6", name: "عائشة اليمني", nationality: "يمنية", age: 38, program: "العقيدة الميسرة", circle: "حلقة الإيمان", joinDate: "1446/01/20", attendance: 82 },
  { id: "wb7", name: "خديجة البلوشي", nationality: "سعودية", age: 26, program: "التجويد", circle: "حلقة النور", joinDate: "1446/03/05", attendance: 90 },
  { id: "wb8", name: "ريم الأحمدي", nationality: "سعودية", age: 22, program: "تحفيظ القرآن", circle: "حلقة البركة", joinDate: "1446/04/01", attendance: 97 },
];

const DEMO_CIRCLES = [
  { id: "cr1", name: "حلقة الفجر", type: "تحفيظ", teacher: "أ. نورة الدوسري", students: 35, schedule: "يومياً 4-6م", location: "مصلى النساء — مسجد التوحيد", progress: 72, curriculum: "حفظ من سورة البقرة إلى آل عمران", color: "#db2777" },
  { id: "cr2", name: "حلقة النور", type: "تجويد", teacher: "أ. هند المطيري", students: 28, schedule: "الأحد والأربعاء 4-5م", location: "مصلى النساء — مسجد الإيمان", progress: 60, curriculum: "أحكام النون الساكنة والتنوين", color: "#7c3aed" },
  { id: "cr3", name: "حلقة الهدى", type: "فقه", teacher: "أ. سارة العتيبي", students: 25, schedule: "الاثنين 5-6:30م", location: "قاعة الجمعية", progress: 45, curriculum: "كتاب الصلاة والصيام", color: "#2563eb" },
  { id: "cr4", name: "حلقة الإيمان", type: "تحفيظ", teacher: "أ. عبير الشمري", students: 30, schedule: "يومياً 9-11ص", location: "مصلى النساء — مسجد السلام", progress: 55, curriculum: "حفظ جزء عم وتبارك", color: C.teal },
  { id: "cr5", name: "حلقة البركة", type: "تحفيظ", teacher: "أ. نورة الدوسري", students: 22, schedule: "يومياً 9-11ص", location: "مصلى النساء — مسجد التوحيد", progress: 80, curriculum: "مراجعة وتثبيت الحفظ", color: "#ea580c" },
  { id: "cr6", name: "حلقة براعم الإيمان", type: "تحفيظ", teacher: "أ. فاطمة القحطاني", students: 40, schedule: "السبت والاثنين 3-4:30م", location: "قاعة الأطفال", progress: 65, curriculum: "حفظ جزء عم للأطفال", color: "#059669" },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16,
  },
  sectionTitle: {
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px",
  },
  toolbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  /* Highlight & Activity Cards */
  highlightCard: {
    display: "flex", alignItems: "center", gap: 12, padding: 10,
    background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`,
  },
  highlightIcon: {
    width: 40, height: 40, borderRadius: 10, display: "flex",
    alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  activityCard: {
    display: "flex", alignItems: "center", gap: 12, padding: 10,
    background: C.bg, borderRadius: 10,
  },
  activityDate: {
    width: 48, height: 48, borderRadius: 10, background: C.white,
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", border: "2px solid", flexShrink: 0,
  },

  /* Teachers Grid */
  teachersGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16,
  },
  teacherCard: { padding: 18, cursor: "pointer" },
  teacherTop: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  teacherAvatar: {
    width: 46, height: 46, borderRadius: 12,
    background: `linear-gradient(135deg, #db2777, #f472b6)`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 18,
  },
  progTag: {
    padding: "3px 8px", background: `#db277712`, color: "#db2777",
    borderRadius: 6, fontSize: 11, fontWeight: 600,
  },
  teacherStats: {
    display: "flex", gap: 20, marginTop: 10, paddingTop: 10,
    borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textLight,
  },

  /* Circles Grid */
  circlesGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16,
  },
  circleCard: { padding: 20, cursor: "pointer" },
  circleHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  circleIcon: {
    width: 44, height: 44, borderRadius: 10, display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  circleStats: {
    display: "flex", gap: 20, marginTop: 10, fontSize: 12, color: C.textLight,
  },
  progressBg: {
    height: 6, borderRadius: 3, background: C.bg, overflow: "hidden",
  },
  progressFill: {
    height: "100%", borderRadius: 3, transition: "width 0.5s",
  },
};
